import React from "react";
import { Grid, Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import { Step, TOTAL_STEPS } from "../../../../../util/enums";
import FooterBar from "./FooterBar";
import TopAuth from "./TopAuth";
import ProgressBar from "./ProgressBar";
import StepHeader from "./StepHeader";
import Button from "../../../../../components/Form/Button";
import { SIGNUP_COLORS, SIGNUP_LAYOUT } from "../../tokens";
import confettiAnim from "../../../../../assets/img/animations/confetti.json";
import logo from "../../../../../assets/img/logo.png";

interface RightSideWrapperProps {
  accountHasBeenCreated?: boolean;
  actualStep?: Step;
  isLoading?: boolean;
  formByStep?: React.ReactNode;
  centerContent?: boolean;
}

const TOP_SAFE_SPACE_PX = 96;

const RightSideWrapper: React.FC<RightSideWrapperProps> = ({
  accountHasBeenCreated,
  actualStep,
  isLoading,
  formByStep,
  centerContent,
}) => {
  const { t: translate } = useTranslation();
  const step = actualStep ?? Step.FIRST;

  const visualStep = accountHasBeenCreated ? TOTAL_STEPS - 1 : step + 1;
  const progressPct = accountHasBeenCreated
    ? 100
    : Math.max(0, Math.min(100, (visualStep / TOTAL_STEPS) * 100));

  const tOr = (k: string, fb: string) => {
    const v = translate(k);
    return v === k ? translate(fb) : v;
  };

  const getHeading = () => {
    switch (step) {
      case Step.SECOND:
        return {
          title: tOr("signup.right.step2Title", "signup.right.createAccount"),
          subtitle: tOr("signup.right.step2Subtitle", "signup.right.subtitle"),
        };
      case Step.THIRD:
        return {
          title: tOr("signup.right.step3Title", "signup.right.createAccount"),
          subtitle: tOr("signup.right.step3Subtitle", "signup.right.subtitle"),
        };
      case Step.FOURTH:
        return {
          title: tOr("signup.right.step4Title", "signup.right.createAccount"),
          subtitle: tOr("signup.right.step4Subtitle", "signup.right.subtitle"),
        };
      case Step.FIFTH:
        return {
          title: tOr("signup.right.step5Title", "signup.right.createAccount"),
          subtitle: tOr("signup.right.step5Subtitle", "signup.right.subtitle"),
        };
      default:
        return {
          title: translate("signup.right.createAccount"),
          subtitle: translate("signup.right.subtitle"),
        };
    }
  };

  const { title, subtitle } = getHeading();

  const centerThisStep =
    typeof centerContent === "boolean"
      ? centerContent
      : step === Step.FIRST || step === Step.SECOND || step === Step.THIRD;

  const showHeaderHere = step !== Step.FOURTH && step !== Step.FIFTH;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          overflowY: "auto",
          minHeight: 0,
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          scrollPaddingTop: `${6 + TOP_SAFE_SPACE_PX}px`,
        }}
      >
        <ProgressBar value={accountHasBeenCreated ? 100 : progressPct} />

        {/* Logo - visible only on xs screens */}
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            display: { xs: "flex", md: "none" },
            maxWidth: "120px",
            height: "auto",
            pt: accountHasBeenCreated ? "60px" : "24px",
            pl: "24px",
            position: "absolute",
          }}
        />

        <TopAuth />

        {accountHasBeenCreated && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 20, md: 24 },
              left: { xs: 16, md: 48 },
              zIndex: 2,
            }}
          >
            <Button
              onClick={() => (window.location.href = "https://shipeezi.com")}
              sx={{
                border: `1px solid ${SIGNUP_COLORS.primary}`,
                color: SIGNUP_COLORS.primary,
                backgroundColor: "transparent",
                borderRadius: SIGNUP_LAYOUT.borderRadius,
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 600,
                textTransform: "none",
                height: { xs: "32px", md: SIGNUP_LAYOUT.buttonHeight },
                px: { xs: 2, md: 3 },
                "&:hover": {
                  backgroundColor: "rgba(0,85,164,0.04)",
                },
              }}
            >
              {translate("signup.right.returnToWebsite")}
            </Button>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            pt: `${TOP_SAFE_SPACE_PX}px`,
            pb: { xs: 12, md: 16 },
            maxWidth: SIGNUP_LAYOUT.maxWidthOtherSteps,
            mx: "auto",
            px: { xs: 2, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: centerThisStep ? "center" : "flex-start",
          }}
        >
          {accountHasBeenCreated ? (
            <Box
              sx={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                flexDirection: "column",
              }}
            >
              <Stack spacing={2} alignItems="center" sx={{ maxWidth: 560, mx: "auto" }}>
                <Box sx={{ width: "105px", height: "105px" }}>
                  <Lottie
                    animationData={confettiAnim}
                    loop={true}
                    autoplay={true}
                    style={{ width: "105px", height: "105px" }}
                  />
                </Box>
                <Typography
                  fontFamily="Manrope"
                  fontWeight={800}
                  fontSize="28px"
                  lineHeight="36px"
                  color={SIGNUP_COLORS.primary}
                >
                  {translate("signup.right.thankYouV2Title")}
                </Typography>
                <Typography
                  fontFamily="Manrope"
                  fontWeight={500}
                  fontSize="16px"
                  lineHeight="24px"
                  color={SIGNUP_COLORS.gray600}
                >
                  {translate("signup.right.thankYouV2Description")}
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Grid
              container
              spacing={0}
              alignItems="flex-start"
              justifyContent="center"
              sx={{ width: "100%" }}
            >
              {showHeaderHere && <StepHeader title={title} subtitle={subtitle} />}
              {formByStep}
            </Grid>
          )}
        </Box>

        <FooterBar />
      </Box>
    </Box>
  );
};

export default RightSideWrapper;
